import { Analytics } from '@/utils/analytics';
import { gql, useMutation } from '@apollo/client';
import { PendingInboundReviewTiming } from '@/stores/order-inbounds-store';
import {
  SetSmartOrderReviewDurationMutation,
  SetSmartOrderReviewDurationMutationVariables,
} from '@/types';
import { useCallback } from 'react';

export const SET_SMART_ORDER_REVIEW_DURATION = gql`
  mutation SetSmartOrderReviewDuration(
    $orderUUIDs: [uuid!]!
    $reviewDurationSeconds: numeric!
  ) {
    update_smart_order(
      where: { uuid: { _in: $orderUUIDs } }
      _set: { review_duration_seconds: $reviewDurationSeconds }
    ) {
      returning {
        uuid
      }
    }
  }
`;

// Reports the full review duration (start -> submission) plus the
// inbound-review-specific duration (start -> Continue) for one staged
// timing.
export const useCompleteInboundReview = () => {
  const [setReviewDuration] = useMutation<
    SetSmartOrderReviewDurationMutation,
    SetSmartOrderReviewDurationMutationVariables
  >(SET_SMART_ORDER_REVIEW_DURATION);

  return useCallback(
    (timing: PendingInboundReviewTiming, submittedAt: number) => {
      const reviewDurationSeconds =
        Math.round((submittedAt - timing.startTime) / 100) / 10;
      const inboundReviewSeconds =
        Math.round((timing.inboundReviewEndTime - timing.startTime) / 100) / 10;

      Analytics.track('order_inbound_detail.review_completed', {
        endSource: 'order_submitted',
        inboundReviewEndSource: timing.inboundReviewEndSource,
        inboundReviewSeconds,
        orderUUID: timing.orderUUID,
        orderUUIDs: timing.orderUUIDs,
        restaurantUUID: timing.restaurantUUID,
        reviewDurationSeconds,
        startSource: timing.startSource,
        supplierUUID: timing.supplierUUID,
      });

      // No status filter here: timing.orderUUIDs is already scoped to what
      // was PENDING at Continue-click time, and shishito flips these to
      // SUBMITTED before this call runs, so a live status check would match
      // nothing.
      setReviewDuration({
        variables: {
          orderUUIDs: timing.orderUUIDs,
          reviewDurationSeconds,
        },
      }).catch(() => {
        // Best-effort persistence - the Analytics event above is the
        // durable record if this write fails.
      });
    },
    [setReviewDuration],
  );
};
